import { Component, OnInit, AfterViewInit, ViewEncapsulation, ViewChild, ElementRef, TemplateRef, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import * as Feather from 'feather-icons';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FournisseurService } from '../../../../services/Fournisseur/fournisseur.service';
import { Fournisseur } from '../../../../models/fournisseur';
import { PaginatedResponse } from '../../../../models/paginatedResponse';
import { EmailComposeComponent } from '../../Vente/devis/parametre-bar/email-compose/email-compose.component';
import { FournisseurEventService } from '../../../../services/Fournisseur/fournisseur-event.service';

@Component({
    selector: 'app-actif-fourniss',
    templateUrl: './actif-fourniss.component.html',
    styleUrls: ['./actif-fourniss.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ActifFournissComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('filterPanel') filterPanel!: ElementRef;
    @ViewChild('bankModal') bankModal!: TemplateRef<any>;
    @ViewChild('phoneModal') phoneModal!: TemplateRef<any>;
    @ViewChild('emailCompose') emailComposeComponent!: EmailComposeComponent;

    fournisseurs: Fournisseur[] = [];
    filteredFournisseurs: Fournisseur[] = [];
    paginatedData: Fournisseur[] = [];
    searchTerm: string = '';
    showFilter: boolean = false;
    fournisseurName: string = '';
    selectedFournisseur: Fournisseur | null = null;
    basicSelectedOption: number = 10;

    totalPages: number = 1;
    totalItems: number = 0;

    searchText = '';
    currentPage = 1;
    perPage = 5;
    pageOptions = [5, 10, 20];

    pages: number[] = [];
    filterSummary: string = '';
    filters: any = {
        raison: '',
        matricule: '',
        chiffre_aff: '',
        operator: '=',
    };
    stationId: number = 0;
    isLoading: boolean = false;
    private subscriptions: Subscription[] = [];

    constructor(
        private modalService: NgbModal,
        private toastr: ToastrService,
        private fournisseurService: FournisseurService,
        private fournisseurEventService: FournisseurEventService,
        private router: Router
    ) {}

    ngOnInit() {
        this.loadUserData();
        this.resetFilters();
        this.loadFournisseurs();
        this.subscriptions.push(
            this.fournisseurEventService.onFournisseurArchiveEvent().subscribe(({ fournisseur, is_archive }) => {
                if (!is_archive) {
                    if (!this.fournisseurs.find((f) => f.id === fournisseur.id)) {
                        this.fournisseurs.push(fournisseur);
                        this.totalItems++;
                        this.updateFilteredData();
                        this.toastr.info(`Fournisseur ${fournisseur.nom_entreprise} ajouté aux actifs (désarchivé)`, 'Info');
                    }
                }
            })
        );
    }

    ngAfterViewInit() {
        setTimeout(() => Feather.replace(), 0);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    loadUserData() {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            this.stationId = user?.stations?.[0]?.id || 0;
            if (!this.stationId) {
                this.toastr.error('Aucune station trouvée. Veuillez créer une station.', 'Erreur');
                this.router.navigate(['/create-station']);
            }
        } catch (error) {
            this.toastr.error('Erreur lors de la récupération des données utilisateur.', 'Erreur');
            this.stationId = 0;
        }
    }

    loadFournisseurs(page: number = this.currentPage, limit: number = this.basicSelectedOption) {
        if (!this.stationId) {
            this.toastr.error('ID de station invalide.', 'Erreur');
            return;
        }
        if (this.isLoading) {
            return;
        }
        this.isLoading = true;
        const sub = this.fournisseurService.getActiveFournisseur(this.stationId, page, limit).subscribe({
            next: (response: PaginatedResponse<Fournisseur>) => {
                this.fournisseurs = response.data || [];
                this.totalItems = response.meta?.total || 0;
                this.totalPages = response.meta?.nb_total_pages || response.meta?.pages || Math.ceil(this.totalItems / this.basicSelectedOption) || 1;
                this.currentPage = response.meta?.page || page;
                if (this.currentPage > this.totalPages) {
                    this.currentPage = this.totalPages;
                    this.loadFournisseurs(this.currentPage, limit);
                    return;
                }
                this.filteredFournisseurs = [...this.fournisseurs];
                this.updateFilteredData();
                this.isLoading = false;

            },
            error: (error) => {
                this.isLoading = false;
                const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement des fournisseurs';
                this.toastr.error(errorMessage, 'Erreur');
                this.fournisseurs = [];
                this.filteredFournisseurs = [];
                this.paginatedData = [];
                this.totalItems = 0;
                this.totalPages = 1;
                this.currentPage = 1;
                this.updatePagination();
            },
        });
        this.subscriptions.push(sub);
    }

    toggleFilter() {
        this.showFilter = !this.showFilter;
        if (this.showFilter) {
            setTimeout(() => Feather.replace(), 0);
        }
    }

    onSearch() {
        this.currentPage = 1;
        this.applyFilters();
    }

    updateFilteredData() {
        this.filteredFournisseurs = this.fournisseurs.filter((fournisseur) => {
            const searchMatch =
                (fournisseur.identifiant_unique || fournisseur.id?.toString() || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                (fournisseur.nom_entreprise || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                (fournisseur.emails || []).some(email => email.email?.toLowerCase().includes(this.searchTerm.toLowerCase()));
            const raisonMatch = !this.filters.raison || (fournisseur.nom_entreprise || '').toLowerCase().includes(this.filters.raison.toLowerCase());
            const matriculeMatch = !this.filters.matricule || (fournisseur.numero_identification_fiscale || '').toLowerCase().includes(this.filters.matricule.toLowerCase());
            const chiffreAffMatch = !this.filters.chiffre_aff || true; // Not implemented
            return searchMatch && raisonMatch && matriculeMatch && chiffreAffMatch;
        });

        if (this.filters.chiffre_aff) {
            this.toastr.warning('Filtre chiffre d\'affaires non implémenté (champ absent dans le modèle)', 'Avertissement');
        }

        this.updateFilterSummary();
        this.updatePagination();
    }

    updateFilterSummary() {
        const activeFilters = [];
        if (this.filters.raison) {
            activeFilters.push(`Raison sociale: ${this.filters.raison}`);
        }
        if (this.filters.matricule) {
            activeFilters.push(`NIF: ${this.filters.matricule}`);
        }
        if (this.filters.chiffre_aff) {
            activeFilters.push(`Chiffre: ${this.filters.operator} ${this.filters.chiffre_aff}`);
        }
        this.filterSummary = activeFilters.join(', ');
    }

    resetFilters() {
        this.filters = { raison: '', matricule: '', chiffre_aff: '', operator: '=' };
        this.searchTerm = '';
        this.currentPage = 1;
        this.loadFournisseurs();
    }

    applyFilters() {
        this.currentPage = 1;
        this.loadFournisseurs();

    }

    updatePagination() {
        this.totalPages = Math.ceil(this.totalItems / this.basicSelectedOption) || 1;
        this.currentPage = Math.min(this.currentPage, this.totalPages);
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);

        this.paginatedData = this.filteredFournisseurs;
    }

    changePage(page: number) {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage && !this.isLoading) {
            this.currentPage = page;
            this.loadFournisseurs(page, this.basicSelectedOption);
        }
    }

    onLimitChange() {
        this.currentPage = 1;
        this.loadFournisseurs(1, this.basicSelectedOption);
    }

    sendEmail(fournisseur: Fournisseur) {
        if (!fournisseur.emails || fournisseur.emails.length === 0 || !fournisseur.emails[0].email) {
            this.toastr.error('Aucune adresse e-mail disponible pour ce fournisseur.', 'Erreur');
            return;
        }
        window.location.href = `mailto:${fournisseur.emails[0].email}`;
    }

    openPhoneModal(fournisseur: Fournisseur) {
        this.selectedFournisseur = fournisseur;
        this.modalService.open(this.phoneModal, { centered: true });
    }

    openEmailModal(fournisseur: Fournisseur) {
        if (!fournisseur.emails || fournisseur.emails.length === 0 || !fournisseur.emails[0].email) {
            this.toastr.error('Aucune adresse e-mail disponible pour ce fournisseur.', 'Erreur');
            return;
        }
        if (!fournisseur.id) {
            this.toastr.error('ID du fournisseur manquant. Impossible d\'envoyer l\'email.', 'Erreur');
            return;
        }
        this.selectedFournisseur = fournisseur;
        if (this.emailComposeComponent) {
            // Pré-remplir l'email du destinataire
            this.emailComposeComponent.email.to = fournisseur.emails[0].email;
            this.emailComposeComponent.openModal();
        } else {
            this.toastr.error('Erreur : Impossible d\'ouvrir le modal d\'email.', 'Erreur');
        }
    }

    viewItem(fournisseur: Fournisseur): void {
        const fournisseurId = fournisseur.id || fournisseur.identifiant_unique;
        if (!fournisseurId) {
            this.toastr.error('ID de fournisseur manquant ou invalide.', 'Erreur');
            return;
        }
        this.router.navigate(['/tiers/fournisseurs/details', fournisseurId], {
            state: { fournisseur },
        });
    }

    editItem(fournisseur: Fournisseur) {
        const fournisseurId = fournisseur.id;
        if (!fournisseurId || isNaN(+fournisseurId)) {
            this.toastr.error('ID de fournisseur manquant ou invalide pour modification.', 'Erreur');
            return;
        }
        this.router.navigate([`/tiers/fournisseurs/edit/${fournisseurId}`], {
            state: { fournisseur, isEditMode: true },
        });
    }

    openModal(fournisseur: Fournisseur, action: string) {
        this.fournisseurName = fournisseur.nom_entreprise || 'N/A';
        this.modalService.open(this.bankModal, { centered: true }).result.then(
            (result) => {
                if (result === 'Confirm click') {
                    this.archiveFournisseur(fournisseur);
                }
            },
            () => {
                this.toastr.info('Archivage annulé', 'Information');
            }
        );
    }

    archiveFournisseur(fournisseur: Fournisseur) {
        if (!fournisseur.id) {
            this.toastr.error('ID de fournisseur manquant pour l\'archivage.', 'Erreur');
            return;
        }
        const sub = this.fournisseurService.archiveFournisseur(fournisseur.id, true).subscribe({
            next: (updatedFournisseur) => {
                this.fournisseurs = this.fournisseurs.filter((f) => f.id !== fournisseur.id);
                this.totalItems--;
                this.updateFilteredData();
                this.fournisseurEventService.emitFournisseurArchiveEvent(updatedFournisseur, true);
                this.toastr.success(`Fournisseur ${fournisseur.nom_entreprise} archivé`, 'Succès');
            },
            error: (error) => {
                const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors de l\'archivage';
                this.toastr.error(errorMessage, 'Erreur');
            },
        });
        this.subscriptions.push(sub);
    }

    clearFilter() {
        this.filterSummary = '';
        this.filters = { raison: '', matricule: '', chiffre_aff: '', operator: '=' };
        this.searchTerm = '';
        this.currentPage = 1;
        this.loadFournisseurs(this.currentPage, this.basicSelectedOption);
    }
}
